import React from 'react';

import { StripeProvider, Elements, injectStripe, CardElement } from 'react-stripe-elements';
import { localize } from 'react-localize-redux';
import { apiPost } from 'helpers/ApiHelper';
import Auth from 'j-toker';

import FlatButton from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';

import TextField from '@material-ui/core/TextField';
import { SyncLoader } from 'halogenium';

import { colors } from 'helpers/ColorHelper';
import { AccentColor } from 'helpers/AccentColorHelper';
import { formatMoney, humanizeSymbol } from 'helpers/Formatter';
import NumberFormat from 'react-number-format';
import stripeConfig from 'config/services/stripe';
import FormHelperText from '@material-ui/core/FormHelperText';
import { getTranslatedString } from 'helpers/i18n';

const paymentFormStyle = {
  base: {
    fontSize: '14px',
    letterSpacing: '0.015em',
    fontFamily: 'inherit',
    fontWeight: 400,
    '::placeholder': {
      color: '#0000004d',
    },
  },
  invalid: {
    color: '#9e2146',
  },
};

const DEBOUNCING_DELAY_IN_MS = 500;

function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={values => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
    />
  );
}

const PaymentForm = injectStripe(
  localize()(
    class PaymentForm extends React.Component {
      UNSAFE_componentWillMount() {
        this.setState({
          form: {
            name: '',
          },
          errors: {},
          submitting: false,
          paymentAmount: null,
          loadingFees: false,
          fees: '$0',
          totalIncludingFees: '$0',
        });
      }

      handleSubmit = ev => {
        ev.preventDefault();

        if (!this.validateForm()) return;
        this.setState({ submitting: true });

        // fields that can be passed to stripe
        // https://stripe.com/docs/stripe-js/reference#stripe-create-token
        this.props.stripe.createToken(this.state.form).then(({ error, token }) => {
          if (token && this.props.customAmount) {
            this.props.onToken(token, this.state.paymentAmount).then(() => {
              this.setState({ submitting: false });
            });
            this.setState({ open: false, error: {} });
          } else if (token) {
            this.props.onToken(token).then(() => {
              this.setState({ submitting: false });
            });
            this.setState({ open: false, error: {} });
          } else if (error) {
            this.setState({ errors: { card: error.message }, submitting: false });
          }
        });
      };

      validateForm = () => {
        // Updates field errors and returns true if the form is valid
        let valid = true;
        Object.keys(this.state.form).forEach(key => {
          const value = this.state.form[key];
          if (!value) {
            this.setState({
              errors: {
                ...this.state.errors,
                [key]: 'Required',
              },
            });
            valid = false;
          }
        });

        if (this.props.customAmount && !this.state.paymentAmount) {
          this.setState({
            errors: {
              ...this.state.errors,
              paymentAmount: 'Required',
            },
          });
        }

        return valid;
      };

      updateForm = field => event => {
        this.setState({
          form: {
            ...this.state.form,
            [field]: event.target.value,
          },
          errors: {
            ...this.state.errors,
            [field]: null,
          },
        });
      };

      updatePaymentAmount = event => {
        const newAmount = event.target.value;
        this.setState({
          paymentAmount: newAmount,
          loadingFees: true,
          errors: {
            ...this.state.errors,
            paymentAmount: null,
          },
        });

        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          apiPost(Auth.getApiUrl() + `/leads/calculate_credit_card_fees`, {
            payment_amount: newAmount,
          })
            .done(response => {
              this.setState({
                loadingFees: false,
                fees: response.fees,
                totalIncludingFees: response.total_including_fees,
              });
            })
            .catch((error, textStatus) => {});
        }, DEBOUNCING_DELAY_IN_MS);
      };

      toggleOpen = () => {
        if (this.props.toggleOpen) {
          this.props.toggleOpen();
        }
        if (!this.state.open) {
          this.setState({ submitting: false });
        }

        this.setState({
          open: !this.state.open,
          paymentAmount: null,
          loadingFees: false,
          fees: '$0',
          totalIncludingFees: '$0',
        });
      };

      render() {
        // Use as a props.open override of internal state
        const isOpen = this.props.open !== undefined ? this.props.open : this.state.open;

        const style = {
          table_header: {
            margin: '5px -20px 10px -20px',
            backgroundColor: '#f1f1f1',
            padding: '5px 10px',
            fontSize: '14px',
            fontWeight: '800',
          },
        };

        // const totalPricing = this.props.pricing ? this.props.pricing['total'] : '';
        const submitButtonVerbiage = this.props.submitButtonVerbiage;

        return (
          <Dialog
            disableBackdropClick
            fullScreen={this.props.fullScreen}
            open={isOpen}
            aria-labelledby="payment-dialog"
            classes={{ paper: 'form-dialog' }}
          >
            {this.state.submitting && (
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  background: 'white',
                  zIndex: '10',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div className="col-xs-12" style={{ textAlign: 'center' }}>
                  <SyncLoader color={AccentColor()} size="16px" margin="auto" />
                  <div className="col-xs-12" style={{ marginTop: '10px' }}>
                    <p style={{ textAlign: 'center' }}>
                      {' '}
                      {getTranslatedString('stripeCheckout.processingPleaseWait')}{' '}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={this.handleSubmit} style={{ height: '100%' }}>
              <DialogTitle id="payment-dialog">
                <h3 style={{ textAlign: 'center', margin: 0 }}>
                  {this.props.title || getTranslatedString('stripeCheckout.confirmPaymentDetails')}
                </h3>
              </DialogTitle>

              <DialogContent>
                <div className="row" style={style.table_header}>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ textAlign: 'center' }}>
                      {getTranslatedString('stripeCheckout.pricingBreakdown')}
                    </span>
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-10 col-xs-offset-1">
                    {this.renderSetPriceBreakdown()}
                    {this.renderManualPriceBreakdown()}
                  </div>
                </div>

                <div className="row" style={style.table_header}>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ textAlign: 'center' }}>
                      {getTranslatedString('paymentDetails')}
                    </span>
                  </div>
                </div>
                <div className="row">
                  <p style={{ textAlign: 'center' }}>
                    {' '}
                    {this.props.additionalVerbiage}{' '}
                    {getTranslatedString('stripeCheckout.stripeDetails')}.
                  </p>
                </div>
                <div className="row" style={{ textAlign: 'center' }}>
                  <div style={{ maxWidth: '300px', margin: 'auto' }}>
                    <p style={{ fontSize: '12px', color: AccentColor() }}>
                      {this.state.errors.card}
                    </p>
                    <CardElement style={paymentFormStyle} />

                    <div className="row">
                      <div className="col-xs-12" style={{ textAlign: 'center', padding: 0 }}>
                        <TextField
                          name="name"
                          label={getTranslatedString('stripeCheckout.cardHolderName')}
                          value={this.state.form.name}
                          error={!!this.state.errors.name}
                          onChange={this.updateForm('name')}
                          style={{
                            width: '100%',
                            maxWidth: '300px',
                            fontSize: '14px',
                            margin: 'auto',
                          }}
                        />
                        <FormHelperText error>{this.state.errors.name}</FormHelperText>
                      </div>
                    </div>
                  </div>
                </div>
                {this.props.dontForcePayment && (
                  <div className="row" style={{ textAlign: 'center' }}>
                    <h5 style={{ fontSize: '10px' }}>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => this.props.onToken(null)}
                      >
                        {getTranslatedString('stripeCheckout.payADifferentWay')}
                      </button>
                    </h5>
                  </div>
                )}
              </DialogContent>

              <div className="row" style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '10px' }}>
                  {getTranslatedString('stripeCheckout.fieldsAreRequired')}
                </div>
                <FlatButton
                  variant="raised"
                  type="submit"
                  disabled={this.state.submitting}
                  style={{
                    color: 'white',
                    backgroundColor: this.state.submitting ? colors.LIGHT_GRAY : AccentColor(),
                  }}
                >
                  {getTranslatedString('pay') || submitButtonVerbiage}
                </FlatButton>
              </div>
            </form>

            <DialogActions style={{ display: 'block' }}>
              <FlatButton
                onClick={this.toggleOpen}
                id="close-button"
                style={{ color: AccentColor() }}
              >
                {getTranslatedString('close')}
              </FlatButton>
            </DialogActions>
          </Dialog>
        );
      }

      renderSetPriceBreakdown = () => {
        if (this.props.customAmount) return;
        if (!this.props.pricing) return;

        const pricingBreakdown = Object.keys(this.props.pricing || {}).map(field => {
          const titleStyle =
            field === 'total' ? { marginTop: '10px', fontSize: '16px', fontWeight: '800' } : {};

          return (
            <div>
              <div
                className="col-xs-8"
                style={{ paddingLeft: '0px', textTransform: 'capitalize', ...titleStyle }}
              >
                <p style={{ margin: '0px' }}>{humanizeSymbol(field)}</p>
              </div>
              <div className="col-xs-4" style={titleStyle}>
                <p style={{ margin: '0px', textAlign: 'right' }}>
                  {formatMoney(this.props.pricing[field])}
                </p>
              </div>
            </div>
          );
        });

        return (
          <div className="row" style={{ marginBottom: '10px', maxWidth: '325px', margin: 'auto' }}>
            {pricingBreakdown}
          </div>
        );
      };

      renderManualPriceBreakdown = () => {
        if (!this.props.customAmount) return;

        return (
          <div className="row" style={{ marginBottom: '10px', maxWidth: '325px', margin: 'auto' }}>
            <div className="row">
              <div className="col-xs-12" style={{ textAlign: 'center', padding: 0 }}>
                <TextField
                  label={getTranslatedString('stripeCheckout.paymentAmount')}
                  value={this.state.paymentAmount}
                  error={!!this.state.errors.paymentAmount}
                  onChange={this.updatePaymentAmount}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    fontSize: '14px',
                    margin: 'auto',
                  }}
                  InputProps={{ inputComponent: NumberFormatCustom }}
                />
                <div style={{ padding: '0 12px 5px' }}>
                  <FormHelperText error>{this.state.errors.paymentAmount}</FormHelperText>
                </div>
              </div>
            </div>

            <div
              className="row"
              style={{ paddingBottom: '15px', borderBottom: '1px dashed grey', margin: '0 15px' }}
            >
              <div className="col-xs-6" style={{ fontFamily: 'Circular-Black', padding: 0 }}>
                {getTranslatedString('stripeCheckout.fee')}
              </div>
              <div className="col-xs-6" style={{ textAlign: 'right', padding: 0 }}>
                {this.state.fees}
              </div>
            </div>
            <div className="row" style={{ padding: '15px 0' }}>
              <div className="col-xs-6" style={{ fontFamily: 'Circular-Black' }}>
                {getTranslatedString('stripeCheckout.totalAmountDue')}
              </div>
              <div className="col-xs-6" style={{ textAlign: 'right' }}>
                {this.state.totalIncludingFees}
              </div>
            </div>
          </div>
        );
      };
    }
  )
);

export default class StripePayment extends React.Component {
  render() {
    return (
      <StripeProvider apiKey={stripeConfig.api}>
        <Elements>
          <PaymentForm {...this.props} />
        </Elements>
      </StripeProvider>
    );
  }
}
